import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeft, ShieldCheck, Clock, Tag, Download, FileText,
  Box, Code, Globe, Database, Wrench, ExternalLink, Key
} from 'lucide-react'
import { useTags, useCVEs } from '@/hooks/useImages'
import { enrichImageData, getSBOMUrl } from '@/lib/api'
import PullCommand from '@/components/PullCommand'
import CVEBadge from '@/components/CVEBadge'
import Loading from '@/components/Loading'
import { useState } from 'react'

const categoryIcons = {
  base: Box,
  runtime: Code,
  web: Globe,
  database: Database,
  tool: Wrench,
}

export default function ImageDetail() {
  const { name } = useParams<{ name: string }>()
  const decodedName = name ? decodeURIComponent(name) : ''
  const [selectedTag, setSelectedTag] = useState('latest')

  const { data: tagsData, isLoading: tagsLoading } = useTags(decodedName)
  const { data: cveData } = useCVEs(decodedName, selectedTag)

  const metadata = enrichImageData(decodedName)
  const Icon = categoryIcons[metadata.category as keyof typeof categoryIcons] || Box

  const tags = tagsData?.tags || []
  const cveCount = cveData?.cveCount ?? 0

  if (tagsLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Loading text="Loading image details..." />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to="/images" className="inline-flex items-center gap-2 text-dark-400 hover:text-white mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" />
        Back to images
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="card p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-4 rounded-xl bg-dark-800 text-primary-400">
                <Icon className="h-8 w-8" />
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold text-white">{decodedName}</h1>
                  <ShieldCheck className="h-5 w-5 text-accent-500" />
                </div>
                <p className="text-dark-400">{metadata.description}</p>
              </div>

              <CVEBadge count={cveCount} size="md" />
            </div>

            <PullCommand imageName={decodedName} tag={selectedTag} />
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Tag className="h-5 w-5 text-dark-400" />
              Available Tags
            </h2>

            {tags.length === 0 ? (
              <p className="text-dark-400">No tags available</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-dark-400 border-b border-dark-700">
                      <th className="pb-3 font-medium">Tag</th>
                      <th className="pb-3 font-medium">CVEs</th>
                      <th className="pb-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-dark-800">
                    {tags.map(tag => (
                      <tr key={tag} className="group">
                        <td className="py-3">
                          <button
                            onClick={() => setSelectedTag(tag)}
                            className={`font-mono text-sm ${
                              selectedTag === tag
                                ? 'text-primary-400'
                                : 'text-dark-200 hover:text-white'
                            }`}
                          >
                            {tag}
                          </button>
                        </td>
                        <td className="py-3">
                          <CVEBadge count={0} />
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <a
                              href={getSBOMUrl(decodedName, tag)}
                              className="p-1.5 rounded hover:bg-dark-700 text-dark-400 hover:text-white transition-colors"
                              title="Download SBOM"
                            >
                              <Download className="h-4 w-4" />
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Key className="h-5 w-5 text-dark-400" />
              Signature Verification
            </h2>

            <p className="text-dark-400 mb-4">
              All ArmoredForge images are signed with Cosign. Verify the signature before deployment:
            </p>

            <div className="terminal">
              <code className="text-accent-400 block">
                <span className="text-dark-500"># Install cosign</span>
              </code>
              <code className="text-accent-400 block mt-2">
                <span className="text-dark-500">$ </span>
                go install github.com/sigstore/cosign/v2/cmd/cosign@latest
              </code>
              <code className="text-accent-400 block mt-4">
                <span className="text-dark-500"># Verify image signature</span>
              </code>
              <code className="text-accent-400 block mt-2">
                <span className="text-dark-500">$ </span>
                cosign verify forge.armored.app/{decodedName}:{selectedTag} \
              </code>
              <code className="text-accent-400 block pl-4">
                --certificate-identity-regexp=".*armoredgate.*" \
              </code>
              <code className="text-accent-400 block pl-4">
                --certificate-oidc-issuer="https://token.actions.githubusercontent.com"
              </code>
            </div>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="card p-6">
            <h3 className="text-sm font-medium text-dark-300 mb-4">Image Details</h3>

            <dl className="space-y-4">
              <div>
                <dt className="text-xs text-dark-500 mb-1">Category</dt>
                <dd className="text-white capitalize">{metadata.category}</dd>
              </div>

              <div>
                <dt className="text-xs text-dark-500 mb-1">Tags</dt>
                <dd className="text-white">{tags.length}</dd>
              </div>

              <div>
                <dt className="text-xs text-dark-500 mb-1">Last Updated</dt>
                <dd className="flex items-center gap-2 text-white">
                  <Clock className="h-4 w-4 text-dark-400" />
                  Today
                </dd>
              </div>

              <div>
                <dt className="text-xs text-dark-500 mb-1">Security</dt>
                <dd>
                  <CVEBadge count={cveCount} />
                </dd>
              </div>
            </dl>
          </div>

          <div className="card p-6">
            <h3 className="text-sm font-medium text-dark-300 mb-4">Resources</h3>

            <div className="space-y-2">
              <a
                href={getSBOMUrl(decodedName, selectedTag)}
                className="flex items-center gap-3 p-3 rounded-lg bg-dark-800 hover:bg-dark-700 transition-colors"
              >
                <FileText className="h-5 w-5 text-dark-400" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">SBOM</p>
                  <p className="text-xs text-dark-400">Software Bill of Materials</p>
                </div>
                <Download className="h-4 w-4 text-dark-400" />
              </a>

              <a
                href={`https://github.com/armoredgate/forge-images/tree/main/${decodedName}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg bg-dark-800 hover:bg-dark-700 transition-colors"
              >
                <Code className="h-5 w-5 text-dark-400" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">Source</p>
                  <p className="text-xs text-dark-400">View build configuration</p>
                </div>
                <ExternalLink className="h-4 w-4 text-dark-400" />
              </a>
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}
